import { useState, useEffect, useRef, useCallback } from 'react';
import { doc as firestoreDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { C_GENERATION_JOBS } from '../constants/collections';
import type { DocDocument, DocTemplate, BrandConfig, AuditReport, GCConflict } from '../types/docops';
import type { GenerationJob, JobDocumentEntry } from '../types/generationJob';
import { saveDocument } from '../services/firestore/docopsService';
import { claudeGenerate } from '../services/ai/providers/claude';
import { runGate, runFallbackGate } from '../services/ai/qualityGate';
import { CF_URLS } from '../config/api';
import { checkDependencies } from '../services/ai/crossDocContext';
import { applyBrandConfig } from '../services/ai/promptBuilder';
import { GEN_MESSAGES } from '../components/admin/documents/docopsConstants';

interface UseDocumentGeneratorInput {
  brandId: string | null;
  config: BrandConfig | null;
  templates: DocTemplate[];
  documents: DocDocument[];
  onDocumentUpdated: (doc: DocDocument) => void;
  refreshDocuments?: () => Promise<void>;
}

export interface GenProgress {
  current: number;
  total: number;
  title: string;
}

export interface AuditProgressInfo {
  attempt: number;
  maxAttempts: number;
  phase: 'generating' | 'scoring' | 'fixing';
  score?: number;
  passed?: boolean;
}

export interface GenResult {
  elapsed: number;
  score?: number;
  passed?: boolean;
  attempts?: number;
  hasGate: boolean;
}

function stripUndefined(obj: unknown): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object') ? stripUndefined(value) : value;
    }
  }
  return cleaned;
}

export function useDocumentGenerator({
  brandId, config, templates, documents, onDocumentUpdated, refreshDocuments,
}: UseDocumentGeneratorInput) {
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [genProgress, setGenProgress] = useState<GenProgress | null>(null);
  const [auditProgress, setAuditProgress] = useState<AuditProgressInfo | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [genMsgIdx, setGenMsgIdx] = useState(0);
  const [genResult, setGenResult] = useState<GenResult | null>(null);
  const [fixingConflicts, setFixingConflicts] = useState(false);
  const [fixProgress, setFixProgress] = useState<{ current: number; total: number; title: string } | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobDocIds, setJobDocIds] = useState<Set<string>>(new Set());
  const [preRegenScores, setPreRegenScores] = useState<Record<string, number>>({});
  const genMsgTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshDocsRef = useRef(refreshDocuments);
  const documentsRef = useRef(documents);
  const onDocUpdatedRef = useRef(onDocumentUpdated);

  // Keep refs in sync
  useEffect(() => { refreshDocsRef.current = refreshDocuments; }, [refreshDocuments]);
  useEffect(() => { documentsRef.current = documents; }, [documents]);
  useEffect(() => { onDocUpdatedRef.current = onDocumentUpdated; }, [onDocumentUpdated]);

  // ── Reconnect to active job after refresh/navigation ──
  useEffect(() => {
    if (!brandId || activeJobId) return;
    const storedJobId = localStorage.getItem('hemologix_active_job');
    if (!storedJobId) return;
    (async () => {
      try {
        const jobRef = firestoreDoc(db, C_GENERATION_JOBS, storedJobId);
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(jobRef);
        if (!snap.exists()) {
          localStorage.removeItem('hemologix_active_job');
          return;
        }
        const job = snap.data() as GenerationJob;
        if (job.brandId !== brandId) {
          localStorage.removeItem('hemologix_active_job');
          return;
        }
        if (job.status === 'queued' || job.status === 'processing') {
          console.log(`[JobQueue] Reconnecting to job ${job.id} (${job.status})`);
          setActiveJobId(job.id);
          setJobDocIds(new Set(job.documents.map(d => d.templateId)));
          setGeneratingAll(true);
          if (job.progress.currentDocTitle) {
            setGenProgress({
              current: job.progress.current + 1,
              total: job.progress.total,
              title: job.progress.currentDocTitle,
            });
            setAuditProgress({
              attempt: job.progress.currentAttempt,
              maxAttempts: job.progress.maxAttempts,
              phase: job.progress.currentDocPhase as 'generating' | 'scoring' | 'fixing',
              score: job.progress.currentScore ?? undefined,
            });
          }
        } else {
          // Job already finished — clean up
          localStorage.removeItem('hemologix_active_job');
        }
      } catch (err) {
        console.warn('[JobQueue] Reconnect failed:', err);
        localStorage.removeItem('hemologix_active_job');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // ── Auto-clear pre-regen scores after 30s (when no job is running) ──
  useEffect(() => {
    if (Object.keys(preRegenScores).length === 0 || activeJobId) return;
    const timer = setTimeout(() => setPreRegenScores({}), 30000);
    return () => clearTimeout(timer);
  }, [preRegenScores, activeJobId]);

  // ── Message rotation for loading UX ──
  useEffect(() => {
    if (generating || generatingAll) {
      setGenMsgIdx(0);
      genMsgTimer.current = setInterval(() => {
        setGenMsgIdx(prev => (prev + 1) % GEN_MESSAGES.length);
      }, 3000);
    } else {
      if (genMsgTimer.current) clearInterval(genMsgTimer.current);
    }
    return () => { if (genMsgTimer.current) clearInterval(genMsgTimer.current); };
  }, [generating, generatingAll]);

  // ── onSnapshot listener for active job ──
  useEffect(() => {
    if (!activeJobId) return;
    const jobRef = firestoreDoc(db, C_GENERATION_JOBS, activeJobId);
    const unsubscribe = onSnapshot(jobRef, async (snap) => {
      if (!snap.exists()) return;
      const job = snap.data() as GenerationJob;

      if (job.status === 'processing') {
        setGeneratingAll(true);
        setGenerating(false);
        // Only update progress if the server has set a doc title
        if (job.progress.currentDocTitle) {
          setGenProgress({
            current: job.progress.current + 1,
            total: job.progress.total,
            title: job.progress.currentDocTitle,
          });
          setAuditProgress({
            attempt: job.progress.currentAttempt,
            maxAttempts: job.progress.maxAttempts,
            phase: job.progress.currentDocPhase as 'generating' | 'scoring' | 'fixing',
            score: job.progress.currentScore ?? undefined,
          });
        }
        // Update completed docs' scores in real-time (so scores appear as each doc finishes)
        for (const entry of job.documents) {
          if (entry.status === 'completed' && entry.auditScore != null) {
            const currentDoc = documentsRef.current.find(d => d.templateId === entry.templateId);
            if (currentDoc && currentDoc.auditScore !== entry.auditScore) {
              onDocUpdatedRef.current({ ...currentDoc, status: 'draft', auditScore: entry.auditScore });
            }
          }
        }
      }

      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        localStorage.removeItem('hemologix_active_job');

        // Refresh documents FIRST (before clearing UI state) so new scores
        // are in the documents array before we stop blanking
        if (refreshDocsRef.current) {
          await refreshDocsRef.current();
        }

        // NOW clear UI state — documents already have new scores
        setGeneratingAll(false);
        setGenerating(false);
        setGenProgress(null);
        setAuditProgress(null);
        setActiveJobId(null);
        setJobDocIds(new Set());

        // Show result summary
        const r = job.results;
        if (job.status === 'cancelled') {
          setGenError(`Job cancelled. ${r.succeeded} completed before cancellation.`);
        } else if (r.failed > 0 || r.skipped > 0) {
          const parts: string[] = [];
          if (r.succeeded > 0) parts.push(`${r.succeeded} generated successfully.`);
          if (r.skipped > 0) parts.push(`${r.skipped} skipped (missing deps).`);
          if (r.failed > 0) parts.push(`${r.failed} failed.`);
          setGenError(parts.join(' '));
        }

        // For single-doc jobs, set genResult
        if (job.jobType === 'single' && job.documents.length === 1) {
          const entry = job.documents[0];
          if (entry.status === 'completed') {
            setGenResult({
              elapsed: entry.elapsed ?? 0,
              score: entry.auditScore,
              passed: entry.auditScore ? entry.auditScore >= 85 : undefined,
              hasGate: true,
            });
          }
        }
      }
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId]);

  // ── Create a server-side generation job ──
  const createJob = useCallback(async (
    eligible: DocDocument[],
    jobType: 'batch' | 'single' | 'round' | 'phase',
    extra?: { phase?: 'blockers' | 'remaining' | 'all'; roundNumber?: number },
  ) => {
    if (!brandId || !config) {
      setGenError('No active brand selected.');
      return;
    }
    if (eligible.length === 0) {
      setGenError('No documents to generate.');
      return;
    }

    const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const jobDocs: JobDocumentEntry[] = eligible.map(d => ({
      docId: d.id,
      templateId: d.templateId,
      title: d.title,
      status: 'pending' as const,
    }));

    const job: GenerationJob = {
      id: jobId,
      brandId,
      ownerId: 'primary',
      jobType,
      phase: extra?.phase,
      roundNumber: extra?.roundNumber,
      documents: jobDocs,
      status: 'queued',
      progress: {
        current: 0,
        total: jobDocs.length,
        currentDocTitle: '',
        currentDocPhase: 'generating',
        currentAttempt: 1,
        maxAttempts: 1,
      },
      results: {
        succeeded: 0, failed: 0, skipped: 0,
        failedDocs: [], skippedDocs: [],
      },
      createdAt: new Date().toISOString(),
      startedAt: null,
      completedAt: null,
      checkpoint: -1,
    };

    // Set UI state IMMEDIATELY (before Firestore write) so score blanks instantly
    setActiveJobId(jobId);
    setGeneratingAll(jobType !== 'single');
    setGenerating(jobType === 'single');
    setGenError(null);
    setGenResult(null);
    setGenProgress({ current: 1, total: jobDocs.length, title: jobDocs[0].title });
    setAuditProgress({ attempt: 1, maxAttempts: 3, phase: 'generating' });
    const newJobDocIds = new Set(jobDocs.map(d => d.templateId));
    setJobDocIds(newJobDocIds);
    // Save pre-regen scores for old → new comparison (keyed by templateId)
    const scores: Record<string, number> = {};
    for (const d of eligible) {
      if (d.auditScore != null) scores[d.templateId] = d.auditScore;
    }
    if (Object.keys(scores).length > 0) {
      setPreRegenScores(prev => ({ ...prev, ...scores }));
    }

    // Optimistically set docs to 'generating' so they move to the Generating stage immediately
    for (const d of eligible) {
      onDocumentUpdated({ ...d, status: 'generating' });
    }

    try {
      await setDoc(firestoreDoc(db, C_GENERATION_JOBS, jobId), stripUndefined(job) as Record<string, unknown>);
      localStorage.setItem('hemologix_active_job', jobId);
      console.log(`[JobQueue] Created job ${jobId}: ${jobDocs.length} docs (${jobType})`);

      // Kick off server-side processing via HTTP
      fetch(CF_URLS.processGenerationJob, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId }),
      }).catch(err => console.warn('[JobQueue] HTTP trigger failed (job still in Firestore):', err));
    } catch (err) {
      // Firestore write failed — roll back UI state
      setActiveJobId(null);
      setGeneratingAll(false);
      setGenerating(false);
      setGenProgress(null);
      setAuditProgress(null);
      setJobDocIds(new Set());
      setGenError(err instanceof Error ? err.message : 'Failed to create generation job');
    }
  }, [brandId, config]);

  // ── Cancel active job ──
  const cancelJob = useCallback(async () => {
    if (!activeJobId) return;
    try {
      await updateDoc(firestoreDoc(db, C_GENERATION_JOBS, activeJobId), { status: 'cancelling' });
      console.log(`[JobQueue] Cancelling job ${activeJobId}`);
    } catch (err) {
      console.error('[JobQueue] Failed to cancel:', err);
    }
  }, [activeJobId]);

  // ── Public API: Generate a single document ──
  async function generateSingle(docId: string) {
    if (!brandId || !config) return;
    const doc = documents.find(d => d.id === docId);
    if (!doc) return;
    const tpl = templates.find(t => t.id === doc.templateId);
    if (!tpl) { setGenError('Template not found for this document.'); return; }

    // Client-side dependency check with confirmation
    const depCheck = checkDependencies(tpl, documents);
    if (!depCheck.ready) {
      const msgs: string[] = [];
      if (depCheck.missing.length > 0) msgs.push(`Missing: ${depCheck.missing.join(', ')}`);
      if (depCheck.lowScore.length > 0) msgs.push(`Low score (<90): ${depCheck.lowScore.join(', ')}`);
      const proceed = window.confirm(
        `Dependency check for "${tpl.title}":\n${msgs.join('\n')}\n\nGenerate anyway? (Cross-document alignment may be reduced)`
      );
      if (!proceed) return;
    }

    await createJob([doc], 'single');
  }

  // ── Public API: Generate a batch ──
  async function generateBatch(eligible: DocDocument[], _label?: string) {
    await createJob(eligible, 'batch');
  }

  // ── Public API: Generate next round ──
  async function generateNextRound(roundInfo: { round: number; docs: DocDocument[]; count: number }) {
    if (!brandId || !config) return;

    if (!window.confirm(
      `Generate Round ${roundInfo.round}: ${roundInfo.count} document(s).\n\nThis will generate all documents in dependency round ${roundInfo.round}. Continue?`
    )) return;

    await createJob(roundInfo.docs, 'round', { roundNumber: roundInfo.round });
  }

  // ── Public API: Generate by phase ──
  async function generateByPhase(phase: 'blockers' | 'remaining' | 'all') {
    if (!brandId || !config) return;

    const phaseLabels = {
      blockers: 'launch blocker documents (with quality gate scoring)',
      remaining: 'remaining documents (non-blocker documents)',
      all: 'ALL documents that are not yet started',
    };
    if (!window.confirm(
      `This will generate AI drafts for ${phaseLabels[phase]}. Documents already in draft/review/final/signed status will be skipped. Continue?`
    )) return;

    let eligible = documents.filter(d => d.status === 'not_started');
    if (phase === 'blockers') {
      const blockers = eligible.filter(d => d.launchBlocker);
      const blockerTemplateIds = new Set(blockers.map(d => d.templateId));
      const neededDeps = new Set<string>();
      for (const doc of blockers) {
        const tpl = templates.find(t => t.id === doc.templateId);
        if (tpl?.dependencies) {
          for (const depId of tpl.dependencies) {
            if (!blockerTemplateIds.has(depId)) {
              const depDoc = eligible.find(d => d.templateId === depId);
              if (depDoc) neededDeps.add(depDoc.id);
            }
          }
        }
      }
      eligible = [...blockers, ...eligible.filter(d => neededDeps.has(d.id))];
    } else if (phase === 'remaining') {
      eligible = eligible.filter(d => !d.launchBlocker);
    }

    await createJob(eligible, 'phase', { phase });
  }

  // ── Rescore ungated documents (client-side, no Claude call) ──
  async function rescoreUngated() {
    if (!brandId || !config) return;
    const unscored = documents.filter(d =>
      d.content && d.status !== 'not_started' && d.auditScore === undefined,
    );
    if (unscored.length === 0) return 0;

    let scored = 0;
    for (const doc of unscored) {
      const tpl = templates.find(t => t.id === doc.templateId);
      if (tpl?.qualityGate) continue;

      const report = runFallbackGate(doc.content, config.brandName);
      const updated: DocDocument = {
        ...doc, auditScore: report.score, auditReport: report,
        lastEditedAt: new Date().toISOString(), lastEditedBy: 'fallback-rescore',
      };
      try {
        await saveDocument(brandId, updated);
        onDocumentUpdated(updated);
        scored++;
      } catch (err) {
        console.error(`Failed to rescore ${doc.title}:`, err);
      }
    }
    return scored;
  }

  // ── Rescore ALL documents (client-side) ──
  async function rescoreAll() {
    if (!brandId || !config) return 0;
    const scoreable = documents.filter(d =>
      d.content && d.status !== 'not_started',
    );
    if (scoreable.length === 0) return 0;

    setGeneratingAll(true);
    setGenError(null);
    let rescored = 0;

    for (let i = 0; i < scoreable.length; i++) {
      const doc = scoreable[i];
      setGenProgress({ current: i + 1, total: scoreable.length, title: `Rescoring: ${doc.title}` });

      const tpl = templates.find(t => t.id === doc.templateId);
      let report;
      if (tpl?.qualityGate) {
        report = runGate(doc.content, tpl.qualityGate, 1);
      } else {
        report = runFallbackGate(doc.content, config.brandName);
      }

      const updated: DocDocument = {
        ...doc, auditScore: report.score, auditReport: report,
        lastEditedAt: new Date().toISOString(), lastEditedBy: 'rescore-all',
      };
      try {
        await saveDocument(brandId, updated);
        onDocumentUpdated(updated);
        rescored++;
      } catch (err) {
        console.error(`Failed to rescore ${doc.title}:`, err);
      }
    }

    setGenProgress(null);
    setGeneratingAll(false);
    return rescored;
  }

  // ── Apply brand config to all documents (client-side) ──
  async function applyConfigToAll() {
    if (!brandId || !config) return;
    const docsWithPlaceholders = documents.filter(d =>
      d.content && /\{\{\w+\}\}/.test(d.content),
    );
    if (docsWithPlaceholders.length === 0) {
      setGenError('No documents contain {{variable}} placeholders to replace.');
      return;
    }
    if (!window.confirm(
      `This will replace {{variable}} placeholders in ${docsWithPlaceholders.length} document(s) with your current brand config values. Continue?`
    )) return;

    setGeneratingAll(true);
    setGenError(null);
    let replaced = 0;

    for (let i = 0; i < docsWithPlaceholders.length; i++) {
      const doc = docsWithPlaceholders[i];
      setGenProgress({ current: i + 1, total: docsWithPlaceholders.length, title: doc.title });
      const newContent = applyBrandConfig(doc.content, config);
      if (newContent !== doc.content) {
        const updated: DocDocument = {
          ...doc, content: newContent, version: doc.version + 1,
          lastEditedAt: new Date().toISOString(), lastEditedBy: 'config-apply',
        };
        try {
          await saveDocument(brandId, updated);
          onDocumentUpdated(updated);
          replaced++;
        } catch (err) {
          console.error(`Failed to apply config to ${doc.title}:`, err);
        }
      }
    }
    setGenProgress(null);
    setGeneratingAll(false);
    if (replaced > 0) setGenError(null);
  }

  // ── Fix a single conflict (client-side, uses Claude proxy) ──
  async function fixOneConflict(
    conflict: GCConflict,
    currentDocs: DocDocument[],
    configOverrides?: Partial<BrandConfig>,
  ): Promise<DocDocument | null> {
    if (!brandId || !config) return null;

    const effectiveConfig = configOverrides ? { ...config, ...configOverrides } : config;
    const doc = currentDocs.find(d => d.templateId === conflict.documentA);
    if (!doc || !doc.content) return null;

    const tpl = templates.find(t => t.id === doc.templateId);

    const systemPrompt = `You are a legal document editor. You fix specific issues in legal documents using surgical patches. You return ONLY the search/replace blocks needed — never the full document.`;
    const userPrompt = `DOCUMENT: "${doc.title}"
---
${doc.content}
---

CONFLICT TO FIX:
Type: ${conflict.type}
Severity: ${conflict.severity}
Description: ${conflict.description}
Recommendation: ${conflict.recommendation}
${conflict.documentB ? `Related document: ${conflict.documentB}` : ''}

BRAND CONFIG:
- Brand: ${effectiveConfig.brandName} (${effectiveConfig.brandEntity})
- Medical Director: ${effectiveConfig.medicalDirectorName || '[TBD]'} (${effectiveConfig.pcEntityName})
- CEO: ${effectiveConfig.ceoName || '[TBD]'}
- Trust: ${effectiveConfig.trustName || '[Family Trust]'}

INSTRUCTIONS:
Return one or more surgical patches to fix this conflict. For each patch, copy the EXACT text from the document that needs changing, then provide the replacement.

If the fix requires ADDING new text (not replacing existing), use an empty SEARCH block and put the new text in REPLACE, with a comment showing where to insert it.

Format (return ONLY this, no commentary):
<<<SEARCH>>>
[exact existing text to find — must match the document verbatim]
<<<REPLACE>>>
[corrected text]

You may return multiple SEARCH/REPLACE blocks if the fix touches several places.`;

    const t0 = performance.now();
    const patchResponse = await claudeGenerate('claude-sonnet-4-5-20250929', systemPrompt, userPrompt, 0.2, 4096);
    const elapsed = Math.round((performance.now() - t0) / 1000);
    console.log(`[ConflictFix] Patch for ${doc.title} in ${elapsed}s`);

    const patches: Array<{ search: string; replace: string }> = [];
    const blocks = patchResponse.split('<<<SEARCH>>>').slice(1);
    for (const block of blocks) {
      const parts = block.split('<<<REPLACE>>>');
      if (parts.length === 2) {
        patches.push({ search: parts[0].trim(), replace: parts[1].trim() });
      }
    }

    let fixedContent = doc.content;
    if (patches.length === 0) {
      fixedContent = patchResponse;
    } else {
      let applied = 0;
      for (const patch of patches) {
        if (patch.search === '' || patch.search.startsWith('INSERT') || patch.search.startsWith('ADD')) {
          fixedContent = fixedContent.trimEnd() + '\n\n' + patch.replace;
          applied++;
        } else if (fixedContent.includes(patch.search)) {
          fixedContent = fixedContent.replace(patch.search, patch.replace);
          applied++;
        }
      }
      if (applied === 0) fixedContent = patchResponse;
    }

    const gate = tpl?.qualityGate;
    let auditReport: AuditReport;
    if (gate) {
      auditReport = runGate(fixedContent, gate, 1);
    } else {
      auditReport = runFallbackGate(fixedContent, config.brandName);
    }

    const now = new Date().toISOString();
    const updatedDoc: DocDocument = {
      ...doc, content: fixedContent, version: doc.version + 1,
      lastEditedAt: now, lastEditedBy: 'ai-conflict-fix',
      auditScore: auditReport.score, auditReport,
    };
    await saveDocument(brandId, updatedDoc);
    onDocumentUpdated(updatedDoc);
    return updatedDoc;
  }

  async function fixConflict(conflict: GCConflict, configOverrides?: Partial<BrandConfig>) {
    if (!brandId || !config) { setGenError('No active brand selected.'); return; }
    const targetDoc = documents.find(d => d.templateId === conflict.documentA);
    if (!targetDoc || !targetDoc.content) {
      setGenError(`Cannot fix: document for "${conflict.documentA}" not found or has no generated content.`);
      return;
    }

    setFixingConflicts(true);
    setFixProgress({ current: 1, total: 1, title: `Fixing: ${targetDoc.title}` });
    setGenError(null);

    try {
      const result = await fixOneConflict(conflict, documents, configOverrides);
      if (!result) setGenError(`AI fix returned no result for "${targetDoc.title}".`);
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Conflict fix failed');
    } finally {
      setFixingConflicts(false);
      setFixProgress(null);
    }
  }

  async function fixAllConflicts(conflicts: GCConflict[]) {
    if (!brandId || !config) return;
    const fixable = conflicts.filter(c =>
      c.status !== 'pending' && documents.some(d => d.templateId === c.documentA && d.content),
    );
    if (fixable.length === 0) return;

    setFixingConflicts(true);
    setGenError(null);
    let currentDocs = [...documents];

    for (let i = 0; i < fixable.length; i++) {
      setFixProgress({ current: i + 1, total: fixable.length, title: fixable[i].description.slice(0, 60) });
      try {
        const updated = await fixOneConflict(fixable[i], currentDocs);
        if (updated) currentDocs = currentDocs.map(d => d.id === updated.id ? updated : d);
      } catch (err) {
        console.error(`[ConflictFix] Failed on ${fixable[i].documentA}:`, err);
      }
    }

    setFixingConflicts(false);
    setFixProgress(null);
  }

  return {
    generating,
    generatingAll,
    fixingConflicts,
    fixProgress,
    genProgress,
    auditProgress,
    genError,
    genResult,
    genMsgIdx,
    activeJobId,
    jobDocIds,
    preRegenScores,
    generateSingle,
    generateBatch,
    generateNextRound,
    generateByPhase,
    applyConfigToAll,
    rescoreUngated,
    rescoreAll,
    fixConflict,
    fixAllConflicts,
    cancelJob,
    clearGenError: () => setGenError(null),
    clearGenResult: () => setGenResult(null),
  };
}
